import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Mail, Phone, CreditCard, User, Calendar, Building2, Scale, Ruler, MapPin, Shield, FileText, ClipboardList, Droplets, MessageCircle, Activity, ChevronRight, AlertCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HEALTH_SECTIONS = [
  {
    icon: FileText,
    label: 'Recetas Medicas',
    description: 'Recetas emitidas al cliente',
    path: 'prescriptions',
    color: '#EF4444',
    gradient: 'from-red-500 to-rose-500',
  },
  {
    icon: Droplets,
    label: 'Analiticas de Sangre',
    description: 'Resultados de laboratorio del cliente',
    path: 'blood-tests',
    color: '#3B82F6',
    gradient: 'from-blue-500 to-sky-500',
  },
];

const DistributorClientProfilePage = () => {
  const { clientId } = useParams();
  const navigate = useNavigate();
  const [client, setClient] = useState(null);
  const [healthTests, setHealthTests] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [clientsRes, testsRes] = await Promise.all([
          axios.get(`${API}/users/clients`),
          axios.get(`${API}/health-tests/client/${clientId}`),
        ]);
        const found = clientsRes.data.find(c => c.id === clientId);
        setClient(found || null);
        setHealthTests(Array.isArray(testsRes.data) ? testsRes.data : []);
      } catch (error) { console.error('Failed to load client profile:', error); } finally { setLoading(false); }
    };
    fetchData();
  }, [clientId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 rounded-full border-4 border-blue-100 border-t-blue-500 animate-spin" />
      </div>
    );
  }

  if (!client) {
    return (
      <div className="max-w-2xl mx-auto">
        <button onClick={() => navigate('/distributor/clients')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-clients">
          <ArrowLeft size={16} /> Clientes
        </button>
        <div className="bg-white rounded-3xl border border-gray-100/50 shadow-sm p-10 text-center mt-5">
          <p className="font-bold text-gray-700 text-lg">Cliente no encontrado</p>
        </div>
      </div>
    );
  }

  const infoItems = [
    { icon: Mail, label: 'Correo', value: client.email },
    { icon: Phone, label: 'Telefono', value: client.phone },
    { icon: CreditCard, label: 'Cedula', value: client.cedula },
    { icon: User, label: 'Genero', value: client.gender },
    { icon: Building2, label: 'Empresa', value: client.company },
    { icon: Shield, label: 'Seguro Medico', value: client.medical_insurance },
    { icon: Ruler, label: 'Estatura', value: client.height },
    { icon: Scale, label: 'Peso', value: client.weight },
    { icon: MapPin, label: 'Direccion', value: client.address },
  ].filter(i => i.value);

  return (
    <div className="max-w-2xl mx-auto space-y-5 animate-fade-in pb-4" data-testid="distributor-client-profile">
      <button onClick={() => navigate('/distributor/clients')} className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-700 transition-colors" data-testid="back-to-clients">
        <ArrowLeft size={16} /> Clientes
      </button>

      {/* Profile header card */}
      <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-3xl p-5 text-white shadow-lg shadow-blue-500/20" data-testid="client-profile-header">
        <div className="flex items-center gap-4">
          <div className="w-16 h-16 rounded-2xl bg-white/20 backdrop-blur flex items-center justify-center flex-shrink-0">
            <span className="text-3xl font-bold">{client.name?.charAt(0)?.toUpperCase()}</span>
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-bold text-xl truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{client.name}</p>
            {client.display_id && (
              <span className="inline-block text-xs bg-white/20 backdrop-blur rounded-full px-3 py-0.5 mt-1 font-mono">
                {client.display_id}
              </span>
            )}
            {client.created_at && (
              <div className="flex items-center gap-1.5 mt-1.5">
                <Calendar size={11} className="text-white/50" />
                <span className="text-xs text-white/70">
                  Desde {new Date(client.created_at).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })}
                </span>
              </div>
            )}
          </div>
        </div>
        <button
          onClick={() => navigate('/distributor/messages')}
          className="mt-4 w-full bg-white/15 backdrop-blur rounded-xl py-2.5 flex items-center justify-center gap-2 text-sm font-bold hover:bg-white/25 transition-colors"
          data-testid="message-client-btn"
        >
          <MessageCircle size={16} /> Enviar mensaje
        </button>
      </div>

      {/* Info cards */}
      <div>
        <p className="text-sm font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Informacion Personal</p>
        <div className="grid grid-cols-2 gap-2.5">
          {infoItems.map((item, i) => {
            const IconComp = item.icon;
            return (
              <div key={item.label} className="bg-white rounded-2xl border border-gray-100/50 p-3.5 shadow-sm" data-testid={`info-${item.label.toLowerCase().replace(/\s+/g, '-')}`}>
                <div className="flex items-center gap-2 mb-1">
                  <IconComp size={13} className="text-blue-500" />
                  <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400">{item.label}</p>
                </div>
                <p className="text-sm font-semibold text-gray-900 truncate">{item.value}</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Health Tests Inline */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <p className="text-sm font-bold text-gray-900" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Tests de Salud</p>
          <span className="text-xs text-gray-400 font-medium">{healthTests.length} completado{healthTests.length !== 1 ? 's' : ''}</span>
        </div>
        {healthTests.length === 0 ? (
          <div className="bg-white rounded-2xl border border-gray-100/50 shadow-sm p-6 text-center" data-testid="no-health-tests">
            <div className="w-14 h-14 bg-gradient-to-br from-violet-50 to-purple-50 rounded-xl flex items-center justify-center mx-auto mb-3">
              <AlertCircle size={24} className="text-violet-300" />
            </div>
            <p className="font-semibold text-gray-600 text-sm">Sin tests de salud</p>
            <p className="text-xs text-gray-400 mt-1">Este cliente aun no ha completado tests de salud</p>
          </div>
        ) : (
          <div className="space-y-2.5">
            {healthTests.map((test, idx) => (
              <button
                key={test.id}
                onClick={() => navigate(`/distributor/clients/${clientId}/forms/${test.id}`)}
                className="w-full bg-white rounded-2xl border border-gray-100/50 overflow-hidden shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 text-left group"
                style={{ animationDelay: `${idx * 60}ms` }}
                data-testid={`health-test-card-${test.id}`}
              >
                <div className="h-1 bg-gradient-to-r from-violet-400 via-purple-500 to-indigo-500" />
                <div className="p-4 flex items-center gap-3.5">
                  <div className="w-11 h-11 bg-gradient-to-br from-violet-500 to-purple-500 rounded-xl flex items-center justify-center flex-shrink-0 shadow-lg shadow-violet-500/20 group-hover:scale-105 transition-transform duration-300">
                    <Activity size={20} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm">Test de Salud Fisica 360</p>
                    <div className="flex items-center gap-1.5 mt-1">
                      <Calendar size={12} className="text-gray-400" />
                      <span className="text-xs text-gray-500 font-medium">
                        {new Date(test.completed_at).toLocaleDateString('es-DO', { day: 'numeric', month: 'long', year: 'numeric' })}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 text-violet-400 group-hover:text-violet-600 transition-colors">
                    <span className="text-xs font-bold">Ver</span>
                    <ChevronRight size={16} />
                  </div>
                </div>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Other Health sub-sections */}
      <div>
        <p className="text-sm font-bold text-gray-900 mb-3" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>Mi Cuerpo</p>
        <div className="space-y-2.5">
          {HEALTH_SECTIONS.map((section, idx) => {
            const IconComp = section.icon;
            return (
              <button
                key={section.path}
                onClick={() => navigate(`/distributor/clients/${clientId}/${section.path}`)}
                className="w-full bg-white rounded-2xl border border-gray-100/50 p-4 text-left shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 group"
                style={{ animationDelay: `${idx * 60}ms` }}
                data-testid={`section-${section.path}`}
              >
                <div className="flex items-center gap-3.5">
                  <div
                    className={`rounded-2xl bg-gradient-to-br ${section.gradient} flex items-center justify-center shadow-lg flex-shrink-0 group-hover:scale-105 transition-transform duration-300`}
                    style={{ width: '46px', height: '46px', boxShadow: `0 4px 14px ${section.color}30` }}
                  >
                    <IconComp size={22} className="text-white" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-sm" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>{section.label}</p>
                    <p className="text-xs text-gray-400 mt-0.5">{section.description}</p>
                  </div>
                  <div className="text-gray-300 group-hover:text-gray-500 transition-colors">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m9 18 6-6-6-6"/></svg>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default DistributorClientProfilePage;
